/**
 * AdPlacement — thin promotional card that appears in calculator pages for free
 * users. Reskinned to the HomeLyph Pro palette (amber accent on card surface)
 * instead of the old blue / green gradient boxes.
 *
 * Pro users see nothing.
 */
import React, { useContext } from 'react';
import { AuthContext } from '../../App';
import { Sparkles, ShoppingBag, GraduationCap, ArrowRight } from 'lucide-react';

const CONTENT = {
  header: {
    title: 'Unlock more with Tier One Pro',
    description: 'Advanced analytics, saved plans, and no in-app promos.',
    cta: 'See Pro plans',
    link: '/pricing',
    Icon: Sparkles,
  },
  footer: {
    title: 'Need supplies?',
    description: 'Freeze-dryer supplies, packaging, and accessories in the store.',
    cta: 'Visit store',
    link: '/store',
    Icon: ShoppingBag,
  },
  'results-top': {
    title: 'Learn the fundamentals',
    description: 'Structured courses from operating home units to running a small business.',
    cta: 'Explore courses',
    link: '/learning',
    Icon: GraduationCap,
  },
};

const AdPlacement = ({ placement = 'header' }) => {
  const { user } = useContext(AuthContext);

  // Only free / anonymous users see promos
  const shouldShow = !user || !user.subscription_tier || user.subscription_tier === 'free';
  if (!shouldShow) return null;

  const ad = CONTENT[placement];
  if (!ad) return null;
  const { Icon } = ad;

  return (
    <div
      className="mb-4 rounded-2xl border border-border bg-card p-4 flex flex-col md:flex-row md:items-center gap-4"
      data-testid={`ad-placement-${placement}`}
    >
      <div className="h-11 w-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
        <Icon className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
          Sponsored
        </div>
        <div className="font-heading font-extrabold text-foreground tracking-tight">
          {ad.title}
        </div>
        <p className="text-sm text-muted-foreground">{ad.description}</p>
      </div>
      <a
        href={ad.link}
        className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90 transition-colors"
      >
        {ad.cta} <ArrowRight className="w-3.5 h-3.5" />
      </a>
    </div>
  );
};

export default AdPlacement;
